import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  createContext,
  useContext,
} from 'react';
import type { ReactNode } from 'react';
import classNames from 'classnames';

import { SideMenu } from 'components/shared/side-menu/index';
import {
  SIDEMENU_COLLAPSED_KEY,
  SIDEMENU_WIDTH,
} from 'components/shared/side-menu/side-menu.constants';
import ImpersonationBanner from 'components/shared/others/impersonation-banner/impersonation-banner';

import { useImpersonation } from 'hooks/use-impersonation';

interface SideMenuContextValue {
  isCollapsed: boolean;
  toggleCollapsed: () => void;
  setTemporarilyCollapsed: (value: boolean) => void;
}

export const SideMenuContext = createContext<SideMenuContextValue>({
  isCollapsed: false,
  toggleCollapsed: () => {},
  setTemporarilyCollapsed: () => {},
});

export const useSideMenuContext = () => useContext(SideMenuContext);

export const useHasImpersonationBanner = (): boolean => {
  const { impersonatingUser, isImpersonating } = useImpersonation();

  return Boolean(impersonatingUser || isImpersonating());
};

interface SideMenuLayoutProps {
  children?: ReactNode;
  rightDock?: ReactNode;
}

const SideMenuLayout = ({ children, rightDock }: SideMenuLayoutProps) => {
  const hasImpersonation = useHasImpersonationBanner();

  const [isPersistedCollapsed, setIsPersistedCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEMENU_COLLAPSED_KEY) === 'true';
    } catch (e) {
      console.warn('Failed to read sidemenu state from localStorage:', e);

      return false;
    }
  });
  const [isTemporarilyCollapsed, setIsTemporarilyCollapsed] = useState(false);

  const isCollapsed = isPersistedCollapsed || isTemporarilyCollapsed;

  const toggleCollapsed = useCallback(() => {
    const nextCollapsed = !isCollapsed;

    setIsPersistedCollapsed(nextCollapsed);

    try {
      localStorage.setItem(SIDEMENU_COLLAPSED_KEY, String(nextCollapsed));
    } catch (e) {
      console.warn('Failed to save sidemenu state to localStorage:', e);
    }

    setIsTemporarilyCollapsed(false);
  }, [isCollapsed]);

  const setTemporarilyCollapsed = useCallback((value: boolean) => {
    setIsTemporarilyCollapsed(value);
  }, []);

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === SIDEMENU_COLLAPSED_KEY) {
        try {
          setIsPersistedCollapsed(e.newValue === 'true');
        } catch (err) {
          console.warn('Failed to handle storage change:', err);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const contentMargin = isCollapsed
    ? SIDEMENU_WIDTH.collapsedMargin
    : SIDEMENU_WIDTH.expandedMargin;
  const bannerLeft = isCollapsed
    ? SIDEMENU_WIDTH.collapsedLeft
    : SIDEMENU_WIDTH.expandedLeft;

  const contextValue = useMemo(
    () => ({ isCollapsed, toggleCollapsed, setTemporarilyCollapsed }),
    [isCollapsed, toggleCollapsed, setTemporarilyCollapsed]
  );

  return (
    <SideMenuContext.Provider value={contextValue}>
      <div className="flex min-h-screen">
        <SideMenu />

        {hasImpersonation && (
          <div
            className={classNames(
              'fixed top-0 right-0 z-[90] transition-all duration-300',
              bannerLeft
            )}
          >
            <ImpersonationBanner />
          </div>
        )}

        <div
          className={classNames(
            'flex-1 flex flex-col min-w-0 transition-all duration-300',
            contentMargin,
            { 'pt-12': hasImpersonation }
          )}
        >
          {children}
        </div>

        {rightDock !== undefined && (
          <div
            className={classNames('sticky top-0 h-screen shrink-0 self-start', {
              'pt-12': hasImpersonation,
            })}
          >
            {rightDock}
          </div>
        )}
      </div>
    </SideMenuContext.Provider>
  );
};

export default SideMenuLayout;
